import React, { useState, useEffect } from 'react';
import axios from '../../config/axiosInstance';
import { Table, Button, Space, message, Input, Select, Row, Col } from 'antd';
import { EyeOutlined, PlusOutlined, SearchOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import LeadPolicyEditorForm from './LeadPolicyEditorForm';

const LeadPolicyEditorList = ({ eventId }) => {
  const [controls, setControls] = useState([]);
  const [filteredControls, setFilteredControls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [formInitialData, setFormInitialData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Function for loading input controls
  const fetchLeadPolicies = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/lead-policy');
      setControls(response.data);
      setFilteredControls(response.data);
    } catch (error) {
      message.error('Error fetching lead policies');
      console.error('Error fetching lead policies:', error);
    } finally {
      setLoading(false);
    }
  };

  // GET Event
  const fetchEvents = async () => {
    try {
      const response = await axios.get('/events');
      setEvents(response.data);
    } catch (error) {
      message.error('Error fetching events');
      console.error('Error fetching events:', error);
    }
  };

  useEffect(() => {
    fetchEvents();
    fetchLeadPolicies();
  }, []);

  const handleView = async (record) => {
    try {
      setLoading(true);
      const { data } = await axios.get(`/lead-policy/${record._id}`);

      setFormInitialData({
          ...data,
          event: record.event,
      });

      setIsEditing(true);
      setFormVisible(true);
    } catch (err) {
      message.error('Error fetching policy details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setIsEditing(false);
    setFormInitialData({ event: selectedEvent });
    setFormVisible(true);
  };

  const handleCloseModal = () => {
    setFormVisible(false); 
    setFormInitialData(null); 
  };

  const handleDelete = async (controlId) => {
    setLoading(true);
    try {
      await axios.delete(`/lead-policy/${controlId}`);
      message.success('Policy deleted successfully');
      fetchLeadPolicies();
    } catch (error) {
      console.error('Error deleting policy:', error.message);
      message.error('Error deleting policy');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchControls = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);

    const filtered = controls.filter((control) =>
      control.name.toLowerCase().includes(value) ||
      control.policyRules.toLowerCase().includes(value) ||
      dayjs(control.createdAt).format('DD-MM-YYYY').includes(value)
    );

    setFilteredControls(filtered);
  };

  const handleEventChange = (eventId) => {
    const selected = events.find((event) => event.eventId === eventId);
    setSelectedEvent(selected);
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => {
        const nameA = a.name?.toLowerCase() || '';
        const nameB = b.name?.toLowerCase() || '';
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: 'Event',
      dataIndex: 'event',
      key: 'event',
      render: (event) => event ? event.mnemonic : 'N/A', 
      sorter: (a, b) => {
        const nameA = a.event?.mnemonic?.toLowerCase() || '';
        const nameB = b.event?.mnemonic?.toLowerCase() || '';
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      responsive: ['md', 'lg'],
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      render: (text) => dayjs(text).format('DD-MM-YYYY'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          />
          <Button
            style={{ color: 'red' }}
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record._id)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px'}}>
      <h1 style={{ marginBottom: '24px' }}>Lead Policy Editor</h1>
      <Row gutter={[16, 16]} align="middle" style={{ marginBottom: '24px' }}> 
        <Col xs={24} sm={12} md={8} lg={6}>
          <Input
            placeholder="Search by name, policy rules, or creation date"
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={handleSearchControls}
            style={{ }}
          />
        </Col>
        <Col xs={20} sm={12} md={8} lg={6}>
          <Select
            placeholder="Select an event"
            style={{ width: '100%'}}
            onChange={handleEventChange}
            value={selectedEvent?.eventId}
          >
            {events.map((event) => (
              <Select.Option key={event.eventId} value={event.eventId}>
                {event.mnemonic}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={4} sm={24} md={8} lg={4}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            disabled={!selectedEvent}
          >
            <span className="hide-text-mobile">Add Policy</span>
          </Button>
        </Col>
      </Row>
      
      <Table
        columns={columns}
        dataSource={filteredControls}
        rowKey="_id"
        loading={loading}
        scroll={{ x: 'max-content' }}
      />

      {formVisible && (
        <LeadPolicyEditorForm
          visible={formVisible}
          onClose={handleCloseModal}
          fetchLeadPolicies={fetchLeadPolicies}
          initialData={formInitialData}
          isEditing={isEditing}
        />
      )}
    </div>
  );
};

export default LeadPolicyEditorList;